// src/pages/NewIntro.jsx
// Two-part New Intro with Treble Town gradient background.
// Part 1: Landing video   •   Part 2: Learn Your Notes (video + practice)

import React, {
  useRef,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import RealPianoListener from "../components/RealPianoListener";
import VirtualPiano, {
  normalizeNoteName as vpNormalizeNoteName,
} from "../components/VirtualPiano";
import PianoKeyMapCard from "../components/PianoKeyMapCard";

// ---------- ASSETS ----------
const MAIN_VIDEO_NAME = "Treble Town Landing Page Video.mp4";
const LEARN_VIDEO_NAME = "Learn Your Notes.mp4";

const ENCODED_MAIN = "/videos/" + encodeURIComponent(MAIN_VIDEO_NAME);
const RAW_MAIN = "/videos/" + MAIN_VIDEO_NAME;
const LEARN_SRC = "/videos/" + encodeURIComponent(LEARN_VIDEO_NAME);

const TOWN_LEFT_IMG = "/images/bg/town-left.png";
const TOWN_RIGHT_IMG = "/images/bg/town-right.png";
const WIS_IMG = "/images/Wis_.png";
const PAN_IMG = "/images/pan-the-panda.png";

// ---------- PRACTICE PROMPTS ----------
const PROMPTS = [
  ["C"], // 1
  ["D"], // 2
  ["C", "D"], // 3
  ["C", "D", "E"], // 4
  ["C", "C", "G", "G"], // 5
  ["C", "D", "E", "F", "G", "A", "B"], // 6
];

/* ─────────────────────────────────────────────
   NOTE NORMALIZATION (LevelTemplate-style)
   ───────────────────────────────────────────── */

const FLAT_TO_SHARP = {
  DB: "C#",
  EB: "D#",
  GB: "F#",
  AB: "G#",
  BB: "A#",
  CB: "B",
  FB: "E",
};
const EDGE_ENHARMONICS = { "E#": "F", "B#": "C" };
const MIDI_TO_PC = [
  "C",
  "C#",
  "D",
  "D#",
  "E",
  "F",
  "F#",
  "G",
  "G#",
  "A",
  "A#",
  "B",
];

function normalizeGameNoteSafe(note) {
  if (!note) return "";
  let raw = String(note).trim().toUpperCase();
  raw = raw.replaceAll("♯", "#").replaceAll("＃", "#").replaceAll("♭", "B");

  const m = raw.match(/[A-G](#|B)?/);
  if (!m) return "";
  let pc = m[0];

  if (EDGE_ENHARMONICS[pc]) pc = EDGE_ENHARMONICS[pc];
  if (pc.length === 2 && pc[1] === "B") pc = FLAT_TO_SHARP[pc] || pc;

  return vpNormalizeNoteName ? vpNormalizeNoteName(pc) : pc;
}

export function normalizeIncomingNote(any) {
  if (any == null) return "";
  if (Array.isArray(any))
    return normalizeIncomingNote(any[any.length - 1] ?? any[0]);

  if (typeof any === "number" && Number.isFinite(any)) {
    const n = Math.round(any);
    const pc = MIDI_TO_PC[((n % 12) + 12) % 12];
    return normalizeGameNoteSafe(pc);
  }

  if (typeof any === "object" && any.name) {
    return normalizeGameNoteSafe(any.name);
  }

  const s = String(any);
  const first = s.split(/[/,|]/)[0];
  return normalizeGameNoteSafe(first);
}

/* ─────────────────────────────────────────────
   MINI VISUAL KEYBOARD (C4→C5)
   ───────────────────────────────────────────── */

function MiniPromptKeyboard({ highlight = [] }) {
  const [vw, setVw] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024
  );

  useEffect(() => {
    const fn = () => setVw(window.innerWidth);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  // Map "C" → "C4" so C5 never lights up
  const hl = new Set(
    highlight.map((n) => {
      const up = String(n).toUpperCase();
      if (up === "C") return "C4";
      return up;
    })
  );

  const whites = [
    { label: "C4", cls: "C4" },
    { label: "D", cls: "D" },
    { label: "E", cls: "E" },
    { label: "F", cls: "F" },
    { label: "G", cls: "G" },
    { label: "A", cls: "A" },
    { label: "B", cls: "B" },
    { label: "C5", cls: "C5" },
  ];

  const blacks = [
    { cls: "C#", pos: 0 },
    { cls: "D#", pos: 1 },
    { cls: "F#", pos: 3 },
    { cls: "G#", pos: 4 },
    { cls: "A#", pos: 5 },
  ];

  const W = 74;
  const H = 160;
  const BW = 42;
  const BH = 104;
  const whiteCount = whites.length;

  const scale = useMemo(() => {
    const baseWidth = W * whiteCount;
    const allowance = vw < 480 ? 56 : vw < 768 ? 64 : 0;
    const available = Math.max(280, vw - allowance);
    const raw = available / baseWidth;
    return Math.min(1, Math.max(0.78, raw));
  }, [vw, whiteCount]);

  return (
    <div className="w-full flex justify-center">
      <div
        className="relative"
        style={{
          transform: `scale(${scale})`,
          transformOrigin: "top center",
        }}
      >
        <div
          className="relative rounded-2xl border border-black/20 bg-white shadow-sm"
          style={{ width: W * whiteCount, height: H }}
          aria-hidden
        >
          {whites.map((k, i) => {
            const active = hl.has(k.cls);
            return (
              <div
                key={i}
                className={`absolute top-0 border rounded-b-lg text-[12px] flex items-end justify-center pb-2 select-none transition-all ${
                  active
                    ? "bg-blue-100 border-blue-300 ring-2 ring-blue-300 animate-pulse"
                    : "bg-white border-gray-300"
                }`}
                style={{ left: i * W, width: W - 1, height: H }}
                title={k.label}
              >
                <span className="font-semibold text-gray-800">{k.label}</span>
              </div>
            );
          })}

          {blacks.map((b, i) => (
            <div
              key={i}
              className="absolute top-0 rounded-b-md shadow bg-black"
              style={{
                left: b.pos * W + Math.floor(0.72 * W),
                width: BW,
                height: BH,
                zIndex: 3,
                border: "1px solid rgba(0,0,0,0.8)",
              }}
              title={b.cls}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   LEVEL-1-STYLE SQUARE NOTE PROMPTS
   ───────────────────────────────────────────── */

function WarmupSquaresRow({ sequence = [], step = 0, wrongFlash = false }) {
  if (!sequence.length) return null;

  return (
    <div className="mt-3 w-full flex justify-center">
      <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
        {sequence.map((n, i) => {
          const isDone = i < step;
          const isCurrent = i === step;

          const base =
            "w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center " +
            "font-extrabold text-sm sm:text-base transition-all";

          let stateClass = "bg-slate-100 text-slate-700";
          if (isDone) stateClass = "bg-[#6EE7B7] text-slate-900";
          if (isCurrent)
            stateClass =
              "bg-white text-[#4F46E5] border-2 border-[#4F46E5] shadow-sm animate-pulse";

          if (isCurrent && wrongFlash)
            stateClass =
              "bg-red-500 text-white border-2 border-red-600 scale-110";

          return (
            <div key={`${n}-${i}`} className={`${base} ${stateClass}`}>
              {n}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Splash background
   ───────────────────────────────────────────── */

function Scenery() {
  const notes = Array.from({ length: 12 }, (_, i) => ({
    left: `${8 + i * 7}%`,
    delay: `${i * 0.6}s`,
    duration: `${8 + (i % 4)}s`,
    size: 18 + (i % 3) * 6,
    char: i % 2 ? "♪" : "♫",
  }));

  return (
    <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-sky-200 via-sky-100 to-amber-50" />

      <svg
        className="absolute top-[-10%] left-0 w-full h-[50%]"
        viewBox="0 0 100 40"
        preserveAspectRatio="none"
        aria-hidden
      >
        <defs>
          <filter id="blur" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" />
          </filter>
        </defs>
        <g filter="url(#blur)" fill="white" opacity="0.45">
          <ellipse cx="10" cy="18" rx="10" ry="6" />
          <ellipse cx="24" cy="16" rx="12" ry="7" />
          <ellipse cx="43" cy="14" rx="14" ry="8" />
          <ellipse cx="65" cy="17" rx="12" ry="7" />
          <ellipse cx="84" cy="15" rx="10" ry="6" />
        </g>
      </svg>

      <img
        src={TOWN_LEFT_IMG}
        alt=""
        onError={(e) => (e.currentTarget.style.display = "none")}
        className="absolute bottom-0 left-0 max-w-[28vw] w-[28vw] min-w-[180px] select-none"
      />
      <img
        src={TOWN_RIGHT_IMG}
        alt=""
        onError={(e) => (e.currentTarget.style.display = "none")}
        className="absolute bottom-0 right-0 max-w-[28vw] w-[28vw] min-w-[180px] select-none"
      />

      <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-emerald-100/70 to-transparent" />

      <style>{`
        @keyframes tt-float-up {
          0%   { transform: translateY(20vh) translateX(0);   opacity: 0; }
          10%  { opacity: 0.9; }
          50%  { transform: translateY(-10vh) translateX(6px); }
          100% { transform: translateY(-30vh) translateX(-6px); opacity: 0; }
        }
      `}</style>
      {notes.map((n, i) => (
        <span
          key={i}
          style={{
            left: n.left,
            bottom: "-6vh",
            animation: `tt-float-up ${n.duration} linear ${n.delay} infinite`,
            fontSize: n.size,
          }}
          className="absolute text-indigo-700/60"
          aria-hidden
        >
          {n.char}
        </span>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────
   Finish Modal
   ───────────────────────────────────────────── */

function WarmupFinishModal({ open, onClose, onGoLevel1 }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-3xl bg-white shadow-2xl border border-slate-200 p-6 sm:p-8 relative">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 text-slate-400 hover:text-slate-700 text-sm"
          aria-label="Close"
        >
          ✕
        </button>

        <div className="flex items-center justify-center gap-4">
          <img
            src={PAN_IMG}
            alt="Pan"
            className="w-16 h-16 sm:w-20 sm:h-20 object-contain"
            onError={(e) => (e.currentTarget.style.display = "none")}
          />
          <div className="text-center">
            <div className="text-2xl sm:text-3xl font-black text-slate-900">
              Awesome!
            </div>
            <div className="text-sm sm:text-base font-semibold text-slate-700 mt-1">
              You finished the warm-up. 🎉
            </div>
          </div>
          <img
            src={WIS_IMG}
            alt="Wis"
            className="w-16 h-16 sm:w-20 sm:h-20 object-contain"
            onError={(e) => (e.currentTarget.style.display = "none")}
          />
        </div>

        <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
          <button
            type="button"
            onClick={onGoLevel1}
            className="px-6 py-3 rounded-full bg-purple-600 text-white font-extrabold shadow hover:bg-purple-700"
          >
            Go to Level 1
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-3 rounded-full bg-white border border-slate-200 font-bold text-slate-700 hover:bg-slate-50"
          >
            Keep Practicing
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   MAIN PAGE
   ───────────────────────────────────────────── */

export default function NewIntro() {
  const nav = useNavigate();

  // Screens
  const [stage, setStage] = useState("splash"); // "splash" | "learn"
  const [learnPhase, setLearnPhase] = useState("video"); // "video" | "practice"

  // Landing video
  const splashRef = useRef(null);
  const [mainSrc, setMainSrc] = useState(ENCODED_MAIN);
  const [isPlaying, setIsPlaying] = useState(false);
  const [mainVideoFailed, setMainVideoFailed] = useState(false);

  // Practice progress
  const [promptIndex, setPromptIndex] = useState(0);
  const [stepIndex, setStepIndex] = useState(0); // which note within current prompt
  const [showFinishModal, setShowFinishModal] = useState(false);

  // Wrong note flash for squares
  const [wrongFlash, setWrongFlash] = useState(false);
  const wrongFlashTimeoutRef = useRef(null);

  // Debounce for note UI
  const lastAcceptedAtRef = useRef(0);
  const lastAcceptedNoteRef = useRef("");

  // Stable refs for latest progress
  const promptIndexRef = useRef(0);
  const stepIndexRef = useRef(0);

  useEffect(() => {
    promptIndexRef.current = promptIndex;
    // reset step when moving to new prompt
    stepIndexRef.current = 0;
    setStepIndex(0);

    if (promptIndex >= PROMPTS.length) {
      setShowFinishModal(true);
    }
  }, [promptIndex]);

  useEffect(() => {
    stepIndexRef.current = stepIndex;
  }, [stepIndex]);

  // Responsive viewport width
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024
  );

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const isPhone = viewportWidth < 640;

  // Soft small-screen scale
  const mobileScale = useMemo(() => {
    const w = viewportWidth;
    if (w >= 1024) return 1;
    if (w < 350) return 0.93;
    if (w < 390) return 0.95;
    if (w < 430) return 0.97;
    if (w < 520) return 0.985;
    return 1;
  }, [viewportWidth]);

  // Avoid applying CSS transform to a parent of <video> on phones.
  const allowTransformForSplash = !isPhone;
  const allowTransformForLearnVideo = !isPhone || learnPhase !== "video";

  const virtualPianoScale = useMemo(() => {
    const w = viewportWidth;
    if (w >= 768) return 1;
    if (w < 350) return 0.92;
    if (w < 390) return 0.95;
    if (w < 430) return 0.97;
    return 1;
  }, [viewportWidth]);

  const pianoConfig = useMemo(() => {
    const w = viewportWidth;

    if (w < 360)
      return {
        keyWidth: 24,
        whiteKeyHeight: 100,
        blackKeyWidth: 16,
        blackKeyHeight: 62,
      };

    if (w < 480)
      return {
        keyWidth: 28,
        whiteKeyHeight: 118,
        blackKeyWidth: 19,
        blackKeyHeight: 76,
      };

    if (w < 768)
      return {
        keyWidth: 36,
        whiteKeyHeight: 145,
        blackKeyWidth: 23,
        blackKeyHeight: 92,
      };

    if (w < 1024)
      return {
        keyWidth: 50,
        whiteKeyHeight: 170,
        blackKeyWidth: 30,
        blackKeyHeight: 110,
      };

    return {
      keyWidth: 62,
      whiteKeyHeight: 200,
      blackKeyWidth: 36,
      blackKeyHeight: 128,
    };
  }, [viewportWidth]);

  const firstKey = "C4";
  const lastKey = "C5";

  /* ─────────────────────────────────────────────
     Splash video helpers
     ───────────────────────────────────────────── */

  const handleSplashClick = useCallback(async () => {
    const v = splashRef.current;
    if (!v) return;
    try {
      v.playsInline = true;
      if (v.paused) await v.play();
      else v.pause();
    } catch {}
  }, []);

  useEffect(() => {
    if (stage !== "splash") return;
    const v = splashRef.current;
    if (!v) return;
    try {
      v.muted = false;
      v.play().then(() => setIsPlaying(true)).catch(() => {});
    } catch {}
  }, [stage]);

  /* ─────────────────────────────────────────────
     Wrong flash helper
     ───────────────────────────────────────────── */

  const triggerWrongFlash = useCallback(() => {
    setWrongFlash(true);
    if (wrongFlashTimeoutRef.current) {
      clearTimeout(wrongFlashTimeoutRef.current);
    }
    wrongFlashTimeoutRef.current = setTimeout(() => {
      setWrongFlash(false);
      wrongFlashTimeoutRef.current = null;
    }, 280);
  }, []);

  useEffect(() => {
    return () => {
      if (wrongFlashTimeoutRef.current) {
        clearTimeout(wrongFlashTimeoutRef.current);
      }
    };
  }, []);

  /* ─────────────────────────────────────────────
     NOTE HANDLERS – match LearnYourNotes logic
     ───────────────────────────────────────────── */

  // UI-only note handler for squares + keyboard
  const handleDetectedNoteForUI = useCallback(
    (rawNote) => {
      const idx = promptIndexRef.current;
      if (idx >= PROMPTS.length) return;

      const seq = PROMPTS[idx] || [];
      if (!seq.length) return;

      const gotPc = normalizeIncomingNote(rawNote);
      if (!gotPc) return;

      const now = Date.now();
      const lastAt = lastAcceptedAtRef.current;
      const lastPc = lastAcceptedNoteRef.current;

      // Avoid double-counting the same hit immediately
      if (now - lastAt < 110 && gotPc === lastPc) return;

      const currentStep = Math.min(
        stepIndexRef.current,
        Math.max(seq.length - 1, 0)
      );
      const expected = seq[currentStep];
      const expPc = normalizeIncomingNote(expected);
      if (!expPc) return;

      if (gotPc === expPc) {
        lastAcceptedAtRef.current = now;
        lastAcceptedNoteRef.current = gotPc;

        setStepIndex((prev) => {
          const next = prev + 1;
          // Hold on last note; full sequence completion will be handled by onComplete
          if (next >= seq.length) return seq.length - 1;
          return next;
        });
      } else {
        triggerWrongFlash();
      }
    },
    [triggerWrongFlash]
  );

  // Virtual piano completion: advance prompt
  const handlePromptComplete = useCallback(() => {
    setPromptIndex((prev) => {
      if (prev >= PROMPTS.length) return prev;
      return prev + 1;
    });
  }, []);

  // Real-piano completion: advance prompt + auto-restart mic
  const handleRealMicComplete = useCallback(() => {
    setPromptIndex((prev) => {
      const next = prev + 1;
      const total = PROMPTS.length;

      if (next < total) {
        setTimeout(() => {
          const btn = document.querySelector(
            "#newintro-learn-mic-wrapper button"
          );
          if (btn && typeof btn.click === "function") {
            btn.click();
          }
        }, 400);
      }

      return next;
    });
  }, []);

  /* ─────────────────────────────────────────────
     Menus
     ───────────────────────────────────────────── */

  const MenuBar = () => (
    <header className="w-full bg-white/90 backdrop-blur border-b border-black/10">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 flex items-center gap-4">
        <div
          className="text-xl sm:text-2xl font-extrabold tracking-tight text-blue-900 cursor-pointer"
          onClick={() => nav("/")}
        >
          Treble Town
        </div>

        <nav className="ml-auto hidden md:flex items-center gap-6 font-semibold">
          <button onClick={() => nav("/premium")} className="hover:opacity-80">
            PREMIUM
          </button>
          <button onClick={() => nav("/parents")} className="hover:opacity-80">
            PARENTS
          </button>
          <button onClick={() => nav("/store")} className="hover:opacity-80">
            STORE
          </button>
          <button onClick={() => nav("/about")} className="hover:opacity-80">
            ABOUT&nbsp;US
          </button>
          <button
            onClick={() => nav("/login")}
            className="px-4 py-1.5 rounded-full border border-blue-800 text-blue-900 hover:bg-blue-50"
          >
            LOG&nbsp;IN
          </button>
        </nav>

        <button
          onClick={() => nav("/login")}
          className="md:hidden ml-auto px-3 py-1 rounded-full border border-blue-800 text-blue-900"
        >
          LOG&nbsp;IN
        </button>
      </div>
    </header>
  );

  /* ─────────────────────────────────────────────
     Splash
     ───────────────────────────────────────────── */

  const Splash = () => {
    const containerClass =
      "max-w-6xl mx-auto px-3 sm:px-4 w-full " +
      (isPhone ? "min-h-[70vh] flex flex-col justify-center" : "mt-4 sm:mt-6");

    const scaleStyle = allowTransformForSplash
      ? { transform: `scale(${mobileScale})`, transformOrigin: "top center" }
      : undefined;

    return (
      <section className="relative flex-1 w-full">
        <Scenery />

        <div className={containerClass} style={scaleStyle}>
          <div
            className="relative w-full max-w-3xl mx-auto rounded-2xl overflow-hidden shadow-2xl bg-white/90 backdrop-blur"
            onClick={handleSplashClick}
          >
            <div className="relative w-full aspect-video bg-slate-900">
              {!mainVideoFailed ? (
                <video
                  ref={splashRef}
                  src={mainSrc}
                  className="w-full h-full object-cover"
                  controls
                  autoPlay
                  playsInline
                  preload="metadata"
                  muted={false}
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                  onEnded={() => setIsPlaying(false)}
                  onError={() => {
                    if (mainSrc !== RAW_MAIN) {
                      setMainSrc(RAW_MAIN);
                      return;
                    }
                    setMainVideoFailed(true);
                  }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="text-center px-4">
                    <div className="text-white font-extrabold text-sm sm:text-base">
                      Landing video not found
                    </div>
                    <div className="text-white/70 text-[10px] sm:text-xs mt-1">
                      Check the file name & path in /public/videos
                    </div>
                  </div>
                </div>
              )}

              {!isPlaying && !mainVideoFailed && (
                <div className="pointer-events-none absolute inset-0 grid place-items-center">
                  <div className="w-14 h-14 sm:w-20 sm:h-20 rounded-full bg-white/85 shadow-xl border-2 border-blue-700 grid place-items-center">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                      <path d="M8 5v14l11-7L8 5z" fill="black" />
                    </svg>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="mt-5 sm:mt-6 flex justify-center">
            <button
              onClick={() => {
                setStage("learn");
                setLearnPhase("video");
                setPromptIndex(0);
                setStepIndex(0);
                setShowFinishModal(false);
                try {
                  splashRef.current?.pause();
                } catch {}
              }}
              className={
                "rounded-full bg-amber-400 border-[3px] border-indigo-900 " +
                "drop-shadow-lg font-extrabold uppercase " +
                "hover:brightness-105 active:translate-y-[1px] " +
                "px-8 sm:px-12 py-4 sm:py-4.5 " +
                "text-2xl sm:text-3xl " +
                "tracking-[0.16em] sm:tracking-[0.25em] " +
                "w-[92%] max-w-md sm:w-auto"
              }
            >
              START&nbsp;GAME
            </button>
          </div>
        </div>
      </section>
    );
  };

  /* ─────────────────────────────────────────────
     Levels rail (left)
     ───────────────────────────────────────────── */

  const LevelsRail = () => (
    <aside className="hidden md:flex flex-col items-center gap-3 w-16 pt-3">
      <button
        onClick={() => nav("/")}
        className="px-3 py-1.5 rounded-2xl bg-yellow-400 border-2 border-blue-600 shadow font-extrabold text-blue-900 text-[11px]"
      >
        HOME
      </button>
      <button
        onClick={() => nav("/notes")}
        className="mt-1 px-3 py-1 rounded-2xl bg-white border border-blue-500 shadow text-[11px] font-bold text-blue-800"
      >
        NOTES
      </button>
      <div className="mt-2 flex flex-col items-center gap-2">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
          <div
            key={n}
            className="w-8 h-8 rounded-full grid place-items-center text-[11px] font-bold shadow bg-gray-100 text-gray-500"
            title={`Level ${n}`}
          >
            {n}
          </div>
        ))}
      </div>
    </aside>
  );

  /* ─────────────────────────────────────────────
     Learn
     ───────────────────────────────────────────── */

  const Learn = () => {
    const practiceDone = promptIndex >= PROMPTS.length;
    const clampedIndex = Math.min(promptIndex, PROMPTS.length - 1);
    const seqForUI = PROMPTS[clampedIndex] || [];

    const safeStep =
      seqForUI.length === 0
        ? 0
        : Math.min(stepIndex, seqForUI.length - 1);

    const nextExpectedNote =
      !practiceDone && seqForUI.length > 0
        ? seqForUI[safeStep]
        : null;

    // For top keyboard highlight (only next note)
    const keyboardHighlight = nextExpectedNote ? [nextExpectedNote] : [];
    const mapNote = nextExpectedNote || seqForUI[0] || null;

    const outerClass =
      "max-w-6xl mx-auto px-3 sm:px-4 w-full " +
      (isPhone && learnPhase === "video"
        ? "min-h-[70vh] flex flex-col justify-center"
        : "mt-4 sm:mt-6");

    const scaleStyle = allowTransformForLearnVideo
      ? { transform: `scale(${mobileScale})`, transformOrigin: "top center" }
      : undefined;

    return (
      <section className="flex-1 w-full">
        <div className={outerClass} style={scaleStyle}>
          <div className="grid grid-cols-1 md:grid-cols-[140px_1fr_160px] gap-4 items-start">
            <LevelsRail />

            <div className="space-y-4">
              <div className="relative rounded-2xl overflow-hidden shadow-xl bg-white">
                {learnPhase === "video" && (
                  <>
                    <div className="w-full aspect-video bg-slate-900">
                      <video
                        src={LEARN_SRC}
                        className="w-full h-full object-cover"
                        playsInline
                        controls
                        preload="metadata"
                        onEnded={() => setLearnPhase("practice")}
                      />
                    </div>

                    <div className="absolute top-2 right-2">
                      <button
                        onClick={() => setLearnPhase("practice")}
                        className="px-3 py-1 rounded-full bg-white/90 backdrop-blur text-sm font-bold border"
                      >
                        Skip to Practice
                      </button>
                    </div>
                  </>
                )}

                {learnPhase === "practice" && (
                  <div className="bg-white flex flex-col items-center justify-start px-3 sm:px-4 pt-4 pb-6 sm:pb-8">
                    <div className="w-full max-w-4xl mx-auto bg-white rounded-3xl shadow-md px-3 sm:px-4 py-4 border border-slate-100">
                      <div className="flex flex-col items-center">
                        {/* 1. MINI PROMPT KEYBOARD (hero position) */}
                        {!practiceDone && (
                          <div className="mt-1 mb-3 w-full flex justify-center">
                            <MiniPromptKeyboard
                              highlight={keyboardHighlight}
                            />
                          </div>
                        )}

                        {/* 2. TITLE */}
                        <div className="text-center">
                          <div className="text-sm sm:text-base font-extrabold text-slate-800">
                            Learn Your Notes Warm-up
                          </div>
                          <div className="text-[10px] sm:text-xs text-slate-500 mt-0.5">
                            Start listening, then play the notes in order.
                          </div>
                        </div>

                        {/* 3. PLAY NOW (RealPianoListener button) */}
                        {!practiceDone && (
                          <div
                            id="newintro-learn-mic-wrapper"
                            className="mt-3 w-full flex justify-center"
                          >
                            <RealPianoListener
                              targetMelody={seqForUI}
                              startLabel="🎤 Play Now"
                              stopLabel="Stop Listening"
                              showMicHint
                              detectionSensitivity={1.1}
                              toleranceCents={70}
                              containerClassName="w-full max-w-md flex flex-col items-center"
                              buttonClassName="px-10 sm:px-12 py-3 rounded-full bg-[#22C55E] text-white shadow-[0_6px_0_#16A34A] text-base sm:text-lg font-black tracking-wide hover:brightness-105 active:translate-y-[1px]"
                              onComplete={handleRealMicComplete}
                              onNoteDetected={handleDetectedNoteForUI}
                            />
                          </div>
                        )}

                        {/* 4. NOTE PROMPT SQUARES */}
                        {!practiceDone && (
                          <WarmupSquaresRow
                            sequence={seqForUI}
                            step={safeStep}
                            wrongFlash={wrongFlash}
                          />
                        )}

                        {/* 5. Helper line */}
                        {!practiceDone && (
                          <div className="mt-2 text-xs sm:text-sm text-gray-700 text-center">
                            Play these notes on your piano (or the virtual one
                            below).
                          </div>
                        )}

                        {practiceDone && (
                          <div className="mt-3 text-xs sm:text-sm text-slate-700 text-center font-semibold">
                            You played all the warm-up notes. Great job!
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Virtual Piano – like Level 1 bottom section */}
              <div className="rounded-2xl border border-black/10 bg-white shadow p-3 sm:p-4">
                <div className="flex items-center justify-center">
                  <h3 className="text-base sm:text-lg font-extrabold">
                    Virtual Piano
                  </h3>
                </div>

                <div className="mt-1 text-center text-[10px] sm:text-xs text-slate-500">
                  You can tap the keys here if you don&apos;t have your piano
                  nearby.
                </div>

                <div className="mt-3 sm:mt-4 w-full flex justify-center">
                  <div className="w-full max-w-4xl flex justify-center">
                    <div
                      className="inline-block"
                      style={{
                        transform: `scale(${virtualPianoScale})`,
                        transformOrigin: "top center",
                      }}
                    >
                      <VirtualPiano
                        key={`warmup-vp-${promptIndex}`}
                        targetMelody={!practiceDone ? seqForUI : []}
                        onComplete={handlePromptComplete}
                        onNoteClick={handleDetectedNoteForUI}
                        keyWidth={pianoConfig.keyWidth}
                        whiteKeyHeight={pianoConfig.whiteKeyHeight}
                        blackKeyWidth={pianoConfig.blackKeyWidth}
                        blackKeyHeight={pianoConfig.blackKeyHeight}
                        firstKey={firstKey}
                        lastKey={lastKey}
                      />
                    </div>
                  </div>
                </div>

                {/* Prompt chips row (C, D, CD, etc) */}
                <div className="mt-3 sm:mt-4 flex flex-wrap gap-2 items-center justify-center">
                  {PROMPTS.map((seq, i) => {
                    const active = i === promptIndex;
                    const done = i < promptIndex;
                    return (
                      <div
                        key={i}
                        className={`px-3 py-1 rounded-full text-[11px] sm:text-xs font-bold border ${
                          done
                            ? "bg-green-500 text-white border-green-600"
                            : active
                            ? "bg-yellow-200 text-yellow-900 border-yellow-400"
                            : "bg-gray-100 text-gray-700 border-gray-300"
                        }`}
                      >
                        {seq.join(" ")}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Right column */}
            <div className="hidden md:flex flex-col items-center gap-4">
              <PianoKeyMapCard
                expectedNote={mapNote}
                className="w-40 shadow-md"
              />
            </div>
          </div>
        </div>

        <WarmupFinishModal
          open={showFinishModal && learnPhase === "practice"}
          onClose={() => setShowFinishModal(false)}
          onGoLevel1={() => nav("/level-1")}
        />
      </section>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#9BE7FF] via-[#FDF7FF] to-[#FFE8C7] text-gray-900 flex flex-col">
      <MenuBar />
      <main className="flex-1 mx-auto w-full">
        {stage === "splash" ? <Splash /> : <Learn />}
      </main>
      <footer className="py-6 text-center text-xs text-gray-500">
        © {new Date().getFullYear()} Treble Town • Landing
      </footer>
    </div>
  );
}
